/**
 * Every permission string the UI checks, in one place.
 *
 * These mirror `Capabilities/Assessment/AssessmentPolicies.cs`. Keeping them as named constants
 * rather than string literals scattered through components means a typo is a compile error instead
 * of a control that silently never appears - which is exactly how a permission bug hides.
 *
 * Report permissions are grantable through the current ScadsysCore catalogue. Deployed Identity services
 * must consume that catalogue before non-Admin grants become available there.
 */

export const Permissions = {
    // Coarse ScadsysCore permissions, used by the hierarchy controllers.
    STRUCTURE: "assessment:structure",
    MANAGEMENT: "assessment:management",
    ENROLLMENT: "assessment:enrollment",
    ACTIVITIES: "assessment:activities",

    // Assessment structure authoring (assessment groups and assessments).
    ACTIVITIES_READ: "assessment:activities.read",
    ACTIVITIES_WRITE: "assessment:activities.write",

    // Organization-scoped vocabularies: assessment types, group types, mark types, comment types.
    CONFIGURATION_READ: "assessment:configuration.read",
    CONFIGURATION_WRITE: "assessment:configuration.write",

    GRADE_SCALES_READ: "assessment:gradeScales.read",
    GRADE_SCALES_WRITE: "assessment:gradeScales.write",

    // Authoring a draft, publishing it and binding it to a module are separate acts.
    PROCEDURES_READ: "assessment:procedures.read",
    PROCEDURES_WRITE: "assessment:procedures.write",
    PROCEDURES_PUBLISH: "assessment:procedures.publish",
    PROCEDURES_ASSIGN: "assessment:procedures.assign",

    CALCULATIONS_RUN: "assessment:calculations.run",
    CALCULATIONS_OVERRIDE_MANUAL: "assessment:calculations.overrideManual",
    CALCULATIONS_OVERRIDE_FINALIZED: "assessment:calculations.overrideFinalized",

    // Entry, finalization and moderation are deliberately three permissions.
    MARKS_READ: "assessment:marks.read",
    MARKS_WRITE: "assessment:marks.write",
    MARKS_FINALIZE: "assessment:marks.finalize",
    MARKS_MODERATE: "assessment:marks.moderate",
    // Removing stored marks; needed to delete an assessment that already has marks.
    MARKS_DELETE: "assessment:marks.delete",

    REPORTS_READ: "assessment:reports.read",
    REPORTS_WRITE: "assessment:reports.write",

    AUDIT_READ: "assessment:audit.read",
    DATA_ADMINISTER: "assessment:data.administer",

    // Seeding a whole organization's subjects, assessment types, mark types, terms and a starter
    // calculation procedure from a curriculum template.
    CURRICULUM_APPLY: "assessment:curriculum.apply",

    // Platform.
    INTEGRATIONS_MANAGE: "manageIntegrations",
    INTEGRATIONS_EXECUTE: "executeIntegrations",
    // Spelled this way in ScadsysCore too - do not "fix" it here or the check stops matching.
    SCADSYS_IMPORT: "importAsessmentDataFromScadsys",
} as const;

export type Permission = (typeof Permissions)[keyof typeof Permissions];
